/**
 * What reaches a node from others: what is connected into it, and for an Image or Video node
 * also the Reference nodes chosen from its list (picked, "wireless": utils/pickedReferences.ts),
 * after what is connected. Pure, over a workspace's nodes and connections, so the app and the
 * Claude connector (netlify/functions/utils/connector) work it out the same way: what the
 * connector reports a node takes is what the app sends.
 */
import { NodeType, type CanvasNode, type Connection, type ReferenceTag } from '../types';
import { pickedAudioInputs, pickedImageInputs, pickedVideoInputs, type FindShared } from './pickedReferences';

export interface ImageInput {
    /** The connection's id, or picked:<id> for a pick */
    id: string;
    url: string;
    label: string;
    /** A Reference node's tag (Kling tells characters from scenes by it) */
    tag?: ReferenceTag;
}

export interface AudioInput {
    id: string;
    url: string;
    label: string;
    durationSec?: number;
    sizeBytes?: number;
}

export interface VideoInput {
    id: string;
    /** The source node (connected), or the pick's input id: what a clip's trim range is kept under */
    sourceId: string;
    url: string;
    label: string;
}

type Nodes = ReadonlyArray<CanvasNode>;
type Connections = ReadonlyArray<Connection>;

const ENTITY_TYPES: NodeType[] = [NodeType.CHARACTER_NODE, NodeType.PROP_NODE, NodeType.LOCATION_NODE];
const isReferenceType = (type: NodeType) => type === NodeType.MEDIA_REFERENCE || type === NodeType.AUDIO_REFERENCE;
const into = (nodeId: string, connections: Connections) => connections.filter(c => c.targetId === nodeId);
const byId = (nodes: Nodes, id: string) => nodes.find(n => n.id === id);

/** Pictures connected into a node, in the order of the connections */
export const connectedImageInputs = (nodeId: string, nodes: Nodes, connections: Connections, visited = new Set<string>()): ImageInput[] => {
    if (visited.has(nodeId)) return [];
    visited.add(nodeId);
    return into(nodeId, connections).flatMap((c): ImageInput[] => {
        const source = byId(nodes, c.sourceId);
        if (!source) return [];
        const d = source.data;
        if (isReferenceType(source.type) && d.imageUrl) return [{ id: c.id, url: d.imageUrl, label: d.title || 'Reference', tag: d.referenceTag }];
        // An Image node is known by its name once it has one of its own
        if (source.type === NodeType.IMAGE_GENERATOR && d.generatedImageUri) return [{ id: c.id, url: d.generatedImageUri, label: d.title && d.title !== 'Image' ? d.title : 'Gen Image' }];
        if (source.type === NodeType.SKETCH_PAD && d.imageUrl) return [{ id: c.id, url: d.imageUrl, label: 'Sketch' }];
        if (ENTITY_TYPES.includes(source.type)) {
            const name = d.entityData?.name || 'Entity';
            const tag: ReferenceTag = source.type === NodeType.LOCATION_NODE ? 'background' : source.type === NodeType.PROP_NODE ? 'prop' : 'character';
            // Only the first of its pictures (the others stay in the entity node)
            const first = (d.referenceImages || (d.imageUrl ? [d.imageUrl] : []))[0];
            if (first) return [{ id: c.id, url: first, label: name, tag }];
            // With none of its own, the first picture connected into it
            const connected = connectedImageInputs(source.id, nodes, connections, new Set(visited));
            return connected.length > 0 ? [{ id: c.id, url: connected[0].url, label: name, tag }] : [];
        }
        return [];
    });
};

/** Character, Prop and Location nodes connected into a node (their descriptions join an Image node's prompt) */
export const connectedEntities = (nodeId: string, nodes: Nodes, connections: Connections): CanvasNode[] =>
    into(nodeId, connections).map(c => byId(nodes, c.sourceId)).filter((n): n is CanvasNode => !!n && ENTITY_TYPES.includes(n.type));

/** Audio connected into a node (the Video node's music or voice/SFX references) */
export const connectedAudioInputs = (nodeId: string, nodes: Nodes, connections: Connections): AudioInput[] =>
    into(nodeId, connections).flatMap((c): AudioInput[] => {
        const source = byId(nodes, c.sourceId);
        if (!source || !isReferenceType(source.type) || !source.data.audioUrl) return [];
        const d = source.data;
        return [{ id: c.id, url: d.audioUrl!, label: d.audioFileName || d.title || 'Audio', durationSec: d.audioDurationSec, sizeBytes: d.audioSizeBytes }];
    });

/** Clips connected into a node (the Video node's clips to continue or restyle) */
export const connectedVideoInputs = (nodeId: string, nodes: Nodes, connections: Connections): VideoInput[] =>
    into(nodeId, connections).flatMap((c): VideoInput[] => {
        const source = byId(nodes, c.sourceId);
        if (!source || !source.data.videoUri) return [];
        const d = source.data;
        if (isReferenceType(source.type)) return [{ id: c.id, sourceId: source.id, url: d.videoUri!, label: d.videoFileName || d.title || 'Reference clip' }];
        if (source.type !== NodeType.VIDEO_GENERATOR) return [];
        const label = d.title && d.title !== 'Video' ? d.title : (d.prompt?.trim().slice(0, 40) || 'Video');
        return [{ id: c.id, sourceId: source.id, url: d.videoUri!, label }];
    });

/**
 * Text connected into a node that replaces its prompt: a Write node's refined text or draft
 * first, else a (legacy) Text Prompt node's visual description. Null when none gives any.
 */
export const connectedPromptSource = (nodeId: string, nodes: Nodes, connections: Connections): { nodeId: string; text: string } | null => {
    const incoming = into(nodeId, connections);
    for (const c of incoming) {
        const source = byId(nodes, c.sourceId);
        if (source?.type === NodeType.WRITE_NODE) {
            const text = source.data.writeData?.refined?.trim() || source.data.writeData?.draft?.trim();
            if (text) return { nodeId: source.id, text };
        }
    }
    for (const c of incoming) {
        const source = byId(nodes, c.sourceId);
        if (source?.type === NodeType.TEXT_PROMPT && source.data.textPromptData?.visualDescription) {
            return { nodeId: source.id, text: source.data.textPromptData.visualDescription };
        }
    }
    return null;
};

/** An Image or Video node's pictures: what is connected, then its picks (a pick of connected media is used once, as connected) */
export const imageReferenceInputs = (node: CanvasNode, nodes: Nodes, connections: Connections, findShared?: FindShared): ImageInput[] => {
    const connected = connectedImageInputs(node.id, nodes, connections);
    return [...connected, ...pickedImageInputs(node.data, connected.map(input => input.url), id => byId(nodes, id), findShared)];
};

/** A Video node's audio: what is connected, then its picks */
export const audioReferenceInputs = (node: CanvasNode, nodes: Nodes, connections: Connections, findShared?: FindShared): AudioInput[] => {
    const connected = connectedAudioInputs(node.id, nodes, connections);
    return [...connected, ...pickedAudioInputs(node.data, connected.map(input => input.url), id => byId(nodes, id), findShared)];
};

/** A Video node's clips: what is connected, then its picks */
export const videoReferenceInputs = (node: CanvasNode, nodes: Nodes, connections: Connections, findShared?: FindShared): VideoInput[] => {
    const connected = connectedVideoInputs(node.id, nodes, connections);
    return [...connected, ...pickedVideoInputs(node.data, connected.map(input => input.url), id => byId(nodes, id), findShared)];
};
